import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import PageCard from '../../components/ui/PageCard';
import { useAuth } from '../../hooks/useAuth';
import { Contract, DealActivity, deleteContract, getContract, getContractActivity, saveSignatureEmail, sendContractForSignature, updateContractStatus } from '../../api/contractService';
import { generateInvoice, generateInvoiceFromProposal, getInvoices, MagazineInvoice } from '../../api/invoiceService';
import SignatureEmailModal from '../../components/contracts/SignatureEmailModal';
import { ContactPayload } from '../../api/contactsService';
import { openContractPdf } from '../../util/generateContractPdf';
import { getProposalCompanyContacts, updateProposal } from '../../api/proposalService';

const linkButtonClass =
  'inline-block rounded bg-torch-primary px-4 py-2 text-sm font-medium text-white no-underline hover:bg-torch-primary-dark';

const statusClass: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  sent: 'bg-blue-50 text-blue-700',
  signed: 'bg-green-50 text-green-700',
};

const formatMoney = (amount: string | number) => {
  const n = Number(amount);
  if (Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n);
};

const ContractDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, canEditAnyDeal, user } = useAuth();
  const [contract, setContract] = useState<Contract | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [linkedInvoice, setLinkedInvoice] = useState<MagazineInvoice | null>(null);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [sendingSign, setSendingSign] = useState(false);
  const [signatureEmailOpen, setSignatureEmailOpen] = useState(false);
  const [savingSignatureEmail, setSavingSignatureEmail] = useState(false);
  const [signUrl, setSignUrl] = useState<string | null>(null);
  const [activity, setActivity] = useState<DealActivity[]>([]);
  const [companyContacts, setCompanyContacts] = useState<ContactPayload[]>([]);
  const [termsAndConditions, setTermsAndConditions] = useState('');
  const [savingTerms, setSavingTerms] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const loadActivity = async (contractId: number) => {
    const acts = await getContractActivity(contractId);
    setActivity(acts);
  };

  const load = async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getContract(id);
      const proposalTerms = data.terms_and_conditions || data.proposal?.terms_and_conditions || '';
      setContract({ ...data, terms_and_conditions: proposalTerms });
      setTermsAndConditions(proposalTerms);
      setCompanyContacts(data.company_id ? await getProposalCompanyContacts(data.company_id) : []);
      const invoices = await getInvoices();
      const linked = data.proposal_id
        ? invoices.find((inv) => inv.proposal_id === data.proposal_id)
        : invoices.find((inv) => inv.magazine_booking_id === data.magazine_booking_id);
      setLinkedInvoice(linked || null);
      await loadActivity(data.id);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Contract not found.');
      setContract(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const canUpdateStatus =
    canEditAnyDeal || (contract && user?.id && contract.sales_rep_id === user.id);

  const handleStatus = async (status: string) => {
    if (!contract) return;
    setUpdating(true);
    setError(null);
    try {
      const updated = await updateContractStatus(contract.id, status);
      setContract(updated);
      await loadActivity(contract.id);
      if (status === 'signed') await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to update status.');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!contract || !isAdmin || !window.confirm('Delete this contract?')) return;
    await deleteContract(contract.id);
    navigate('/contracts');
  };

  const handleSaveTerms = async () => {
    if (!contract?.proposal_id || contract.status !== 'draft' || contract.proposal?.status !== 'draft') return;
    setSavingTerms(true);
    setError(null);
    try {
      const updatedProposal = await updateProposal(contract.proposal_id, { terms_and_conditions: termsAndConditions });
      setContract((current) => current ? {
        ...current,
        terms_and_conditions: termsAndConditions,
        proposal: current.proposal ? { ...current.proposal, ...updatedProposal, terms_and_conditions: termsAndConditions } : current.proposal,
      } : current);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to save the Terms & Conditions.');
    } finally {
      setSavingTerms(false);
    }
  };

  const canEditTerms = Boolean(
    canUpdateStatus && contract?.proposal_id && contract.status === 'draft' && contract.proposal?.status === 'draft'
  );

  const handleViewPdf = () => {
    if (!contract) return;
    setPdfError(null);
    try {
      openContractPdf({ ...contract, terms_and_conditions: termsAndConditions });
    } catch (e: any) {
      setPdfError(e?.message || 'Unable to generate the contract PDF.');
    }
  };

  const handleCreateInvoice = async () => {
    if (!contract) return;
    setCreatingInvoice(true);
    setError(null);
    try {
      if (contract.status !== 'signed') {
        setError('Invoice can only be generated after the contract is signed.');
        return;
      }
      const invoice = contract.proposal_id
        ? await generateInvoiceFromProposal(contract.proposal_id, { contract_id: contract.id })
        : await generateInvoice(contract.magazine_booking_id!, { contract_id: contract.id });
      navigate(`/invoices/${invoice.id}`);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to create invoice.');
    } finally {
      setCreatingInvoice(false);
    }
  };

  const handleSendForSignature = async (resend: boolean, ccEmail = '') => {
    if (!contract) return;
    setSendingSign(true);
    setError(null);
    try {
      const updated = await sendContractForSignature(contract.id, resend, undefined, ccEmail);
      setContract(updated);
      setSignUrl(updated.sign_url || null);
      await loadActivity(contract.id);
      return true;
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to send for signature.');
      return false;
    } finally {
      setSendingSign(false);
    }
  };

  const handleSaveSignatureEmail = async (emailSubject: string, emailText: string, ccEmail: string, sendAfterSave = false) => {
    if (!contract) return;
    setSavingSignatureEmail(true);
    setError(null);
    try {
      const saved = await saveSignatureEmail(contract.id, emailText, emailSubject, ccEmail);
      setContract((current) => current ? { ...current, ...saved, signature_email_subject: emailSubject, signature_email_text: emailText, email_text: emailText } : current);
      if (sendAfterSave) {
        setSavingSignatureEmail(false);
        if (!(await handleSendForSignature(contract.status === 'sent', ccEmail))) return;
      }
      setSignatureEmailOpen(false);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to save the signature email.');
    } finally {
      setSavingSignatureEmail(false);
    }
  };

  const slot = contract?.magazine_booking?.ad_slot;
  const proposalLines = contract?.proposal?.line_items || [];
  const proposalTotal = proposalLines.reduce((sum, line) => sum + Number(line.price || 0), 0);

  return (
    <Layout>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Contract</h1>
          <p className="mt-1 text-sm text-gray-500">
            {contract?.company?.name || 'Magazine contract'}
          </p>
        </div>
        <div className="flex gap-2">
          {contract && (
            <button
              type="button"
              onClick={handleViewPdf}
              className="rounded border border-torch-primary bg-white px-4 py-2 text-sm font-medium text-torch-primary hover:bg-purple-50"
            >
              View contract as PDF
            </button>
          )}
          {contract?.proposal_id && (
            <Link
              to={`/magazine/proposals/${contract.proposal_id}`}
              className="inline-block rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 no-underline hover:bg-gray-50"
            >
              View proposal
            </Link>
          )}
          <Link to="/contracts" className={linkButtonClass}>
            Back to contracts
          </Link>
        </div>
      </div>

      {loading ? (
        <PageCard><p className="text-sm text-gray-500">Loading...</p></PageCard>
      ) : !contract ? (
        <PageCard><p className="text-sm text-gray-500">{error || 'Not found.'}</p></PageCard>
      ) : (
        <>
          {pdfError && <p className="mb-4 text-sm text-red-600">{pdfError}</p>}
          <PageCard className="mb-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <div className="text-sm font-medium text-gray-600">Status</div>
                <div className="mt-1">
                  <span className={`rounded px-2 py-0.5 text-sm font-medium capitalize ${statusClass[contract.status] || ''}`}>
                    {contract.status}
                  </span>
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-600">Template</div>
                <div className="mt-1 text-sm text-gray-900">{contract.template?.name || '—'}</div>
              </div>
              {contract.proposal_id ? (
                <>
                  <div>
                    <div className="text-sm font-medium text-gray-600">Proposal</div>
                    <div className="mt-1 text-sm text-gray-900">
                      <Link to={`/magazine/proposals/${contract.proposal_id}`} className="text-torch-primary hover:underline">
                        Proposal #{contract.proposal_id}
                      </Link>
                      <br />
                      <span className="text-gray-500 capitalize">{contract.proposal?.status}</span>
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-600">Total</div>
                    <div className="mt-1 text-sm text-gray-900">{formatMoney(proposalTotal)}</div>
                  </div>
                  <div className="sm:col-span-2">
                    <div className="text-sm font-medium text-gray-600">Line items</div>
                    <ul className="mt-1 list-inside list-disc text-sm text-gray-700">
                      {proposalLines.map((line) => (
                        <li key={line.id}>
                          {line.ad_slot?.issue?.publication?.name || '—'} — {line.label} ({formatMoney(line.price || 0)})
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <div className="text-sm font-medium text-gray-600">Publication / issue</div>
                    <div className="mt-1 text-sm text-gray-900">
                      {slot?.issue?.publication?.name || '—'}
                      <br />
                      <span className="text-gray-500">{slot?.issue?.title || '—'}</span>
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-600">Ad slot</div>
                    <div className="mt-1 text-sm text-gray-900">{slot?.label || '—'}</div>
                  </div>
                </>
              )}
              <div>
                <div className="text-sm font-medium text-gray-600">Sales rep</div>
                <div className="mt-1 text-sm text-gray-900">
                  {contract.sales_rep?.username || contract.sales_rep?.email || '—'}
                </div>
              </div>
              <div>
                <div className="text-sm font-medium text-gray-600">Timestamps</div>
                <div className="mt-1 text-sm text-gray-600">
                  Created: {contract.createdAt ? new Date(contract.createdAt).toLocaleString() : '—'}
                  {contract.sent_at && (
                    <>
                      <br />
                      Sent: {new Date(contract.sent_at).toLocaleString()}
                    </>
                  )}
                  {contract.signed_at && (
                    <>
                      <br />
                      Signed: {new Date(contract.signed_at).toLocaleString()}
                    </>
                  )}
                  {contract.signer_name && (
                    <>
                      <br />
                      Signer: {contract.signer_name}
                      {contract.signer_email ? ` (${contract.signer_email})` : ''}
                    </>
                  )}
                </div>
              </div>
            </div>

            {canUpdateStatus && (
              <div className="mt-6 border-t border-gray-200 pt-4">
                {contract.status !== 'signed' && (
                  <>
                    <div className="mb-2 text-sm font-medium text-gray-700">E-sign</div>
                    <div className="mb-4 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        disabled={sendingSign}
                        onClick={() => setSignatureEmailOpen(true)}
                        className="rounded border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm text-blue-800 hover:bg-blue-100 disabled:opacity-50"
                      >
                        {contract.status === 'sent' ? 'Resend for e-signature' : 'Review & send for e-signature'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setSignatureEmailOpen(true)}
                        className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        View/edit email
                      </button>
                      {signUrl && (
                        <span className="text-xs text-gray-500">Link emailed to customer.</span>
                      )}
                    </div>
                  </>
                )}

                <div className="mb-2 text-sm font-medium text-gray-700">Invoice</div>
                <div className="mb-4">
                  {linkedInvoice ? (
                    <Link to={`/invoices/${linkedInvoice.id}`} className="text-sm text-torch-primary hover:underline">
                      View invoice {linkedInvoice.invoice_number}
                    </Link>
                  ) : contract.status === 'signed' ? (
                    <button
                      type="button"
                      disabled={creatingInvoice}
                      onClick={handleCreateInvoice}
                      className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      {creatingInvoice ? 'Creating…' : 'Create invoice'}
                    </button>
                  ) : (
                    <p className="text-sm text-gray-500">Available after contract is signed.</p>
                  )}
                </div>

                <div className="mb-2 text-sm font-medium text-gray-700">Update status</div>
                <div className="flex flex-wrap gap-2">
                  {contract.status !== 'sent' && (
                    <button
                      type="button"
                      disabled={updating}
                      onClick={() => handleStatus('sent')}
                      className="rounded border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm text-blue-800 hover:bg-blue-100 disabled:opacity-50"
                    >
                      Mark as sent
                    </button>
                  )}
                  {contract.status !== 'signed' && isAdmin && (
                    <button
                      type="button"
                      disabled={updating}
                      onClick={() => handleStatus('signed')}
                      className="rounded border border-green-200 bg-green-50 px-3 py-1.5 text-sm text-green-800 hover:bg-green-100 disabled:opacity-50"
                    >
                      Mark as signed (admin)
                    </button>
                  )}
                  {canEditAnyDeal && contract.status !== 'draft' && (
                    <button
                      type="button"
                      disabled={updating}
                      onClick={() => handleStatus('draft')}
                      className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                      Reset to draft
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      type="button"
                      disabled={updating}
                      onClick={handleDelete}
                      className="rounded border border-red-200 px-3 py-1.5 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50"
                    >
                      Delete
                    </button>
                  )}
                </div>
                {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
              </div>
            )}
          </PageCard>

          <PageCard>
            <div className="mb-2 text-sm font-medium text-gray-700">Contract body</div>
            <pre className="whitespace-pre-wrap rounded-lg bg-gray-50 p-4 text-sm text-gray-900">
              {contract.body}
            </pre>
          </PageCard>

          <PageCard className="mt-6">
            <div className="mb-2 text-sm font-medium text-gray-700">Terms &amp; Conditions</div>
            <p className="mb-3 text-xs text-gray-500">
              Magazine-specific terms shown to the customer as part of this contract.
            </p>
            <textarea
              value={termsAndConditions}
              onChange={(event) => setTermsAndConditions(event.target.value)}
              readOnly={!canEditTerms}
              rows={12}
              placeholder="Add the magazine Terms & Conditions for this contract."
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 outline-none focus:border-torch-primary focus:ring-1 focus:ring-torch-primary read-only:bg-gray-50 read-only:text-gray-700"
            />
            {canEditTerms && (
              <div className="mt-3 flex justify-end">
                <button
                  type="button"
                  onClick={handleSaveTerms}
                  disabled={savingTerms || termsAndConditions === (contract.terms_and_conditions || '')}
                  className="rounded bg-torch-primary px-4 py-2 text-sm font-medium text-white hover:bg-torch-primary-dark disabled:opacity-50"
                >
                  {savingTerms ? 'Saving…' : 'Save Terms & Conditions'}
                </button>
              </div>
            )}
            {contract.status === 'signed' && (
              <p className="mt-2 text-xs text-gray-500">Terms are locked because this contract has been signed.</p>
            )}
          </PageCard>

          {activity.length > 0 && (
            <PageCard className="mt-6">
              <h2 className="mb-4 text-sm font-semibold text-gray-900">Activity</h2>
              <ul className="space-y-3">
                {activity.map((entry) => (
                  <li key={entry.id} className="border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                    <div className="text-xs text-gray-500">
                      {new Date(entry.createdAt).toLocaleString()}
                      {' · '}
                      {entry.user?.username || entry.user?.email || 'System'}
                    </div>
                    <div className="mt-0.5 text-sm text-gray-800">{entry.summary}</div>
                  </li>
                ))}
              </ul>
            </PageCard>
          )}
          <SignatureEmailModal
            open={signatureEmailOpen}
            initialSubject={''}
            initialText={contract.signature_email_text || contract.default_signature_email || ''}
            recipientEmail={contract.proposal?.contact_email || contract.signer_email || ''}
            contacts={companyContacts}
            initialCcEmail={contract.signature_email_cc || ''}
            saving={savingSignatureEmail}
            sending={sendingSign}
            status={contract.status}
            onClose={() => setSignatureEmailOpen(false)}
            onSave={(subject, text, ccEmail) => handleSaveSignatureEmail(subject, text, ccEmail)}
            onSaveAndSend={(subject, text, ccEmail) => handleSaveSignatureEmail(subject, text, ccEmail, true)}
          />
        </>
      )}
    </Layout>
  );
};

export default ContractDetail;
